// components
import { SideNavigationLink } from "../../../../../components/SideNavigation";

// hooks
import { useRbacEmployeePermissions, useUser } from "../../../../../api/hooks";

// utils
import getPossessiveNameCase from "../../../../../utils/getPossessiveNameCase";

// local hooks
import useCompetenciesNavigationParams from "../useCompetenciesNavigationParams";

export const getCompetenciesNavigationLinks = (
	employeeId: string,
	employeeName: string | null | undefined,
	rbac: string[] = [],
	hasDirectReports = false
): SideNavigationLink[] => {
	const navLinks: SideNavigationLink[] = [];
	if (rbac.includes("competencies:viewAssignments")) {
		navLinks.push({
			label: `${employeeName ? getPossessiveNameCase(employeeName) : "My"} Competencies`,
			href: `/employees/${employeeId}/competencies/my/`,
			key: "my",
		});
	}
	if (hasDirectReports && rbac.includes("competencies:viewTeamAssignments")) {
		navLinks.push({
			label: `${employeeName ? `${getPossessiveNameCase(employeeName)} Team` : "My Team"} Competencies`,
			href: `/employees/${employeeId}/competencies/team/`,
			key: "team",
		});
	}
	return navLinks;
};

type UseCompetenciesNavLinksAndPermissionsReturn = {
	isLoading: boolean;
	activePage: string;
	navLinks: SideNavigationLink[];
	employeeId: string;
	userId: string;
	directReports: string[];
	hasDirectReports: boolean;
	userCanViewMyCompetencies: boolean;
	userCanViewTeamCompetencies: boolean;
	userCanUpdateEmployeeCurrentLevel: boolean;
	userCanUpdateTeamCurrentLevel: boolean;
	userCanExportCsv: boolean;
};

const useCompetenciesNavLinksAndPermissions = (): UseCompetenciesNavLinksAndPermissionsReturn => {
	const {
		activePage,
		isLoading: isNavParamsLoading,
		userId = "",
		employeeId = "",
	} = useCompetenciesNavigationParams();
	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(
		employeeId,
		"competency_assignment",
		!employeeId
	);
	const { data: employee, isLoading: isUserLoading } = useUser({ userID: employeeId, disabled: !employeeId });

	const firstName = userId !== employeeId ? employee?.name?.split(" ")[0] : null;

	const directReports = employee?.directReports ?? [];
	const hasDirectReports = directReports.length > 0;

	// The team-page update gate must be resolved against a team MEMBER, not the page subject.
	// `updateAssignmentLevel` is a per-report object permission (assignee-manager / chain-of-command),
	// so a manager viewing their OWN team resolves as `self` against their own id and would never get
	// it — only admins (whose primary role grants it against anyone) would. Resolving against a direct
	// report mirrors the backend's object-scoped PATCH check, and one report is representative (a
	// manager/CoC/admin holds the same authority over every member of the team they can see). The query
	// is disabled (no fetch) when there are no reports — the call itself stays unconditional (Rules of Hooks).
	const firstReportId = directReports[0];
	const { data: reportRbac, isLoading: isReportRbacLoading } = useRbacEmployeePermissions(
		firstReportId ?? "",
		"competency_assignment",
		!firstReportId
	);

	return {
		isLoading: isNavParamsLoading || isRbacLoading || isUserLoading || isReportRbacLoading,
		activePage,
		navLinks: getCompetenciesNavigationLinks(employeeId, firstName, rbac, hasDirectReports),
		employeeId,
		userId,
		directReports,
		hasDirectReports,
		userCanViewMyCompetencies: rbac?.includes("competencies:viewAssignments") ?? false,
		userCanViewTeamCompetencies: rbac?.includes("competencies:viewTeamAssignments") ?? false,
		userCanUpdateEmployeeCurrentLevel: rbac?.includes("competencies:updateAssignmentLevel") ?? false,
		userCanUpdateTeamCurrentLevel: reportRbac?.includes("competencies:updateAssignmentLevel") ?? false,
		userCanExportCsv: rbac?.includes("competencies:exportAssignmentsCsv") ?? false,
	};
};

export default useCompetenciesNavLinksAndPermissions;
